import React, { useState } from 'react';
import { Mail, Check, Send } from 'lucide-react';
import { trackGenerateLead } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// Resend hand-off: on submit → backend upserts Resend contact + fires
// protein_calculator.completed event (payload schema enforced server-side).
// UX rule: headline number stays visible on-screen — this gate only emails the protocol.
export const ProteinProtocolGate = ({ weightLb, goalLabel, dailyProteinG, perMealG, mealsPerDay }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/protein-calculator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          first_name: firstName || null,
          weight_lb: weightLb,
          goal_label: goalLabel,
          daily_protein_g: dailyProteinG,
          per_meal_g: perMealG,
          meals_per_day: mealsPerDay,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      trackGenerateLead('protein_calculator_gate', 'tool_protein-calculator');
    } catch (err) {
      console.error('Protein protocol gate submit failed:', err);
      setError('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-[#c8ff00]/5 border border-[#c8ff00]/30 p-5 flex items-center gap-3" data-testid="protein-gate-success">
        <Check className="w-5 h-5 text-[#c8ff00] shrink-0" />
        <p className="text-zinc-300 text-sm">
          Your personalized protein protocol is on its way to <span className="text-[#c8ff00]">{email}</span> — daily target, per-meal split, and food cheat sheet included.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/80 border border-[#c8ff00]/30 p-6" data-testid="protein-gate">
      <p className="text-[#c8ff00] text-[11px] uppercase tracking-[0.2em] font-heading mb-1">Free Protocol Delivery</p>
      <h3 className="text-white text-lg font-heading mb-1">Email me my protein protocol</h3>
      <p className="text-zinc-500 text-sm mb-4">
        Get your {dailyProteinG}g/day target, per-meal breakdown, and a high-protein food cheat sheet delivered to your inbox.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="protein-gate-form">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          className="sm:w-44 px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#c8ff00] focus:outline-none transition-colors"
          disabled={submitting}
          data-testid="protein-gate-name"
        />
        <div className="relative flex-1">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#c8ff00] focus:outline-none transition-colors"
            disabled={submitting}
            required
            data-testid="protein-gate-email"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#c8ff00] text-black text-sm font-heading uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
          data-testid="protein-gate-submit"
        >
          {submitting ? 'Sending…' : 'Send It'} <Send className="w-3.5 h-3.5" />
        </button>
      </form>
      {error && <p className="text-red-400 text-xs mt-2" data-testid="protein-gate-error">{error}</p>}
      <p className="text-[10px] text-zinc-600 mt-3">We&apos;ll never share your email. Unsubscribe any time.</p>
    </div>
  );
};
